import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, OperatorFunction } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { UserSubscription, SubscriptionProduct, SimpleSubscriptionStatus, CadDownloadFeatures } from '../shared/interfaces/subscription.interfaces';
import { AuthService } from './security/auth.service';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  // Cache for CAD download features
  private readonly cadDownloadFeatures = signal<string[]>([]);
  private featuresLoaded = false;


  getCurrentSubscription(): Observable<UserSubscription> {
    return this.http.get<UserSubscription>('/subscription');
  }

  getSubscriptionProducts(): Observable<{ products: SubscriptionProduct[] }> {
    return this.http.get<{ products: SubscriptionProduct[] }>('/subscription/products');
  }

  startFreeTrial(): Observable<object> {
    return this.http.post('/subscription/start-free-trial', {}).pipe(
      this.withTokenRefresh()
    );
  }

  getSubscriptionStatus(): Observable<{ status: SimpleSubscriptionStatus }> {
    return this.http.get<{ status: SimpleSubscriptionStatus }>('/subscription/status');
  }

  /**
   * Refresh user's token to get updated subscription status
   * Call this after any subscription changes (payments, cancellations, etc.)
   */
  refreshSubscriptionStatus(): Observable<object> {
    return this.authService.refreshTokenForSubscriptionUpdate();
  }

  /**
   * Cancel subscription immediately
   */
  cancelSubscription(): Observable<{ status: string; message: string }> {
    return this.http.post<{ status: string; message: string }>('/subscription/cancel-subscription', {}).pipe(
      this.withTokenRefresh()
    );
  }

  /**
   * Cancel subscription at the end of the current billing period
   */
  cancelSubscriptionAtPeriodEnd(): Observable<{ status: string; message: string }> {
    return this.http.post<{ status: string; message: string }>('/subscription/cancel-subscription-at-period-end', {}).pipe(
      this.withTokenRefresh()
    );
  }

  /**
   * Start plugin checkout session
   */
  createPluginCheckoutSession(returnUrl: string): Observable<{ url: string }> {
    return this.http.post<{ url: string }>('/subscription/plugin/checkout', { return_url: returnUrl });
  }

  /**
   * Cancel plugin subscription at the end of the current billing period
   */
  cancelPluginSubscriptionAtPeriodEnd(): Observable<{ status: string; message: string }> {
    return this.http.post<{ status: string; message: string }>('/subscription/plugin/cancel', {}).pipe(
      this.withTokenRefresh()
    );
  }

  /**
   * Resume plugin subscription (undo cancel_at_period_end)
   */
  resumePluginSubscription(): Observable<{ status: string; message: string }> {
    return this.http.post<{ status: string; message: string }>('/subscription/plugin/resume', {}).pipe(
      this.withTokenRefresh()
    );
  }

  /**
   * Create a Stripe billing portal session for managing subscription
   * Opens the Stripe customer portal where users can upgrade, downgrade, update payment, etc.
   */
  openBillingPortal(returnUrl: string): Observable<{ url: string }> {
    return this.http.post<{ url: string }>('/subscription/billing-portal', { return_url: returnUrl });
  }

  /**
   * Load and cache CAD download features from subscription
   */
  loadCadDownloadFeatures(): Observable<string[]> {
    if (this.featuresLoaded) {
      return of(this.cadDownloadFeatures());
    }

    // Admins get all CAD download features
    if (this.authService.isAdmin) {
      const allFeatures = Object.values(CadDownloadFeatures);
      this.cadDownloadFeatures.set(allFeatures);
      this.featuresLoaded = true;
      return of(allFeatures);
    }

    return this.getCurrentSubscription().pipe(
      map((subscription: UserSubscription) => {
        let features: string[] = [];

        // For trial subscriptions, grant all features
        if (subscription.product_snapshot === 'trial') {
          features = Object.values(CadDownloadFeatures);
        } else if (subscription.product_snapshot?.product_cad_download_features) {
          features = subscription.product_snapshot.product_cad_download_features;
        }

        this.cadDownloadFeatures.set(features);
        this.featuresLoaded = true;
        return features;
      })
    );
  }

  /**
   * Get cached CAD download features (synchronous)
   */
  getCadDownloadFeatures(): string[] {
    return this.cadDownloadFeatures();
  }

  /**
   * Check if user can download Organic CAD
   */
  canDownloadOrganic(): boolean {
    const features = this.cadDownloadFeatures();
    return features.includes(CadDownloadFeatures.ORGANIC);
  }

  /**
   * Check if user can download Prismatic CAD
   */
  canDownloadPrismatic(): boolean {
    const features = this.cadDownloadFeatures();
    return features.includes(CadDownloadFeatures.PRISMATIC);
  }

  /**
   * Clear cached features (call on logout or subscription change)
   */
  clearCachedFeatures(): void {
    this.cadDownloadFeatures.set([]);
    this.featuresLoaded = false;
  }

  /**
   * RxJS operator that refreshes the auth token after a subscription change,
   * then returns the original response.
   */
  private withTokenRefresh<T>(): OperatorFunction<T, T> {
    return (source: Observable<T>) => source.pipe(
      switchMap(response =>
        this.authService.refreshTokenForSubscriptionUpdate().pipe(
          switchMap(() => of(response))
        )
      )
    );
  }
}
